import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, MapPin, Users, Clock, ClipboardCheck, ClipboardClock } from 'lucide-react';
import { formatMatchDateFull } from '../utils/dateFormatting';

interface Match {
  id: number;
  opponent_name: string;
  match_date: string;
  location?: string;
  formatted_address?: string;
  latitude?: number;
  longitude?: number;
  home_away?: string;
  notes?: string;
  lineup_published?: boolean;
  team_id: number;
  team?: {
    id: number;
    name: string;
  };
}

interface LineupConfirmation {
  id: number;
  lineup_id: number;
  player_id: number;
  confirmed_at: string;
  confirmed_by: number;
}

interface LineupPosition {
  id: number;
  match_id: number;
  position_number: number;
  player1_id: number;
  player2_id?: number | null;
  confirmations?: LineupConfirmation[] | null;
}

interface PlayerAvailability {
  id: number;
  match_id: number;
  player_id: number;
  availability_status: 'available' | 'unavailable' | 'maybe';
  notes?: string;
  updated_at: string;
}

interface User {
  id: number;
  name: string;
  email: string;
}

interface MatchCardProps {
  match: Match;
  user: User | null;
  matchLineups: { [matchId: number]: LineupPosition[] };
  availability: Map<number, PlayerAvailability>;
}

const MatchCard: React.FC<MatchCardProps> = ({ match, user, matchLineups, availability }) => {
  // Check if user is in lineup
  const isUserInLineup = (): boolean => {
    if (!user || !matchLineups[match.id]) return false;
    
    // Check if lineup is visible (published or match is in past)
    const matchDate = new Date(match.match_date);
    const isPast = matchDate < new Date();
    const isPublished = match.lineup_published === true;
    
    // Lineup indicators only show if lineup is published (or match is in past)
    if (!isPast && !isPublished) return false;
    
    const lineup = matchLineups[match.id];
    return lineup.some(
      position => position.player1_id === user.id || position.player2_id === user.id
    );
  };

  // Check if a player has confirmed their lineup position
  const isPlayerConfirmed = (lineupPosition: LineupPosition, playerId: number): boolean => {
    if (!lineupPosition.confirmations) return false;
    return lineupPosition.confirmations.some(conf => conf.player_id === playerId);
  };

  // Check if user has confirmed their lineup position for a match
  const hasUserConfirmedLineup = (): boolean => {
    if (!user || !matchLineups[match.id]) return false;
    
    // Check if lineup is visible (published or match is in past)
    const matchDate = new Date(match.match_date);
    const isPast = matchDate < new Date();
    const isPublished = match.lineup_published === true;
    
    // Lineup indicators only show if lineup is published (or match is in past)
    if (!isPast && !isPublished) return false;
    
    const lineup = matchLineups[match.id];
    return lineup.some(position => {
      if (position.player1_id === user.id) {
        return isPlayerConfirmed(position, user.id);
      }
      if (position.player2_id === user.id) {
        return isPlayerConfirmed(position, user.id);
      }
      return false;
    });
  };

  // Get availability status for a match
  const getAvailabilityStatus = (): 'available' | 'unavailable' | 'maybe' | null => {
    const avail = availability.get(match.id);
    return avail ? avail.availability_status : null;
  };

  // Check if match is in the past
  const isMatchInPast = (): boolean => {
    return new Date(match.match_date) < new Date();
  };

  // Check if match is currently happening (within 2 hours of match start time)
  const isMatchCurrentlyHappening = (): boolean => {
    const matchStartTime = new Date(match.match_date);
    const matchEndTime = new Date(matchStartTime.getTime() + 2 * 60 * 60 * 1000); // Add 2 hours
    const now = new Date();
    return now >= matchStartTime && now <= matchEndTime;
  };

  const userIsPlaying = isUserInLineup();
  const availabilityStatus = getAvailabilityStatus();
  const isPast = isMatchInPast();
  const hasConfirmed = hasUserConfirmedLineup();
  // Don't show warning if user has confirmed their lineup position
  const showWarning = userIsPlaying && !isPast && availabilityStatus !== 'available' && !hasConfirmed;
  const isLive = isMatchCurrentlyHappening();

  return (
    <Link
      to={`/matches/${match.id}`}
      className={`relative block bg-white dark:bg-gray-800 shadow rounded-lg p-4 md:p-6 transition-all cursor-pointer hover:shadow-lg hover:scale-[1.02] hover:bg-gray-50 dark:hover:bg-gray-700 ${isPast ? 'opacity-60' : ''} ${isLive && !isPast ? 'animate-pulse-border' : ''} ${!isPast && showWarning ? 'ring-2 ring-yellow-400 border-l-4 border-yellow-500' : !isPast && userIsPlaying && !showWarning ? 'ring-2 ring-green-400 border-l-4 border-green-600' : ''}`}
    >
      <div className="flex items-center justify-between gap-4">
        <div className="flex-1 min-w-0 pr-3">
          <div className="flex items-center gap-2 mb-2">
            <h3 className={`text-base md:text-lg font-medium ${isPast ? 'text-gray-500 dark:text-gray-400' : 'text-gray-900 dark:text-gray-100'}`}>
              {match.home_away === 'away' ? 'at' : 'vs'} {match.opponent_name}
            </h3>
            {!isPast && match.lineup_published !== undefined && (
              <div className="relative group">
                {match.lineup_published ? (
                  <ClipboardCheck className="w-5 h-5 text-gray-300 dark:text-gray-600 opacity-70 flex-shrink-0" />
                ) : (
                  <ClipboardClock className="w-5 h-5 text-gray-300 dark:text-gray-600 opacity-70 flex-shrink-0" />
                )}
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-10 transition-opacity">
                  {match.lineup_published ? 'Lineup published' : 'Lineup not published'}
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-px">
                    <div className="border-4 border-transparent border-t-gray-900 dark:border-t-gray-700" />
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="mb-2 flex flex-wrap items-center gap-2">
            {isPast && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300">
                Past
              </span>
            )}
            {!isPast && (showWarning || (userIsPlaying && !showWarning)) && (
              <>
                {showWarning && (
                  <span className="inline-flex items-center px-2 md:px-3 py-1 rounded-full text-xs md:text-sm font-semibold bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 border border-yellow-300 dark:border-yellow-700">
                    <svg className="w-3 h-3 md:w-4 md:h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    You're in the lineup, but not available
                  </span>
                )}
                {userIsPlaying && !showWarning && (
                  <span className="inline-flex items-center px-2 md:px-3 py-1 rounded-full text-xs md:text-sm font-semibold bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 border border-green-300 dark:border-green-700">
                    <svg className="w-3 h-3 md:w-4 md:h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    You're in the lineup
                  </span>
                )}
              </>
            )}
          </div>
          {match.team && (
            <p className={`text-sm mb-2 flex items-center gap-2 ${isPast ? 'text-gray-500 dark:text-gray-500' : 'text-gray-600 dark:text-gray-400'}`}>
              <Users className="w-4 h-4 flex-shrink-0" />
              {match.team.name}
            </p>
          )}
          <div className="space-y-1">
            {(() => {
              const formatted = formatMatchDateFull(match.match_date);
              const textColor = isPast ? 'text-gray-500 dark:text-gray-500' : 'text-gray-600 dark:text-gray-400';
              return (
                <>
                  <p className={`text-sm ${textColor} flex items-center gap-2`}>
                    <Calendar className="w-4 h-4 flex-shrink-0" />
                    {formatted.date}
                  </p>
                  <p className={`text-sm ${textColor} flex items-center gap-2`}>
                    <Clock className="w-4 h-4 flex-shrink-0" />
                    {formatted.time}
                  </p>
                </>
              );
            })()}
            {(match.location || match.formatted_address) && (
              <div className="space-y-1">
                {match.location && (
                  <p className={`text-sm ${isPast ? 'text-gray-500 dark:text-gray-500' : 'text-gray-600 dark:text-gray-400'} flex items-center gap-2`}>
                    <MapPin className="w-4 h-4 flex-shrink-0" />
                    {match.location}
                  </p>
                )}
                {match.formatted_address && (
                  <p className={`flex items-center gap-2 ${match.location ? `text-sm ${isPast ? 'text-gray-500 dark:text-gray-500' : 'text-gray-600 dark:text-gray-400'} ml-6` : `text-sm ${isPast ? 'text-gray-500 dark:text-gray-400' : 'text-gray-600 dark:text-gray-400'}`}`}>
                    {!match.location && <MapPin className="w-4 h-4 flex-shrink-0" />}
                    {match.formatted_address}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="flex-shrink-0 self-center">
          <svg className={`w-4 h-4 ${isPast ? 'text-gray-400 dark:text-gray-500' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </div>
      </div>
    </Link>
  );
};

export default MatchCard;
export type { Match, LineupPosition, PlayerAvailability };

